import { useEffect } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

interface BrandingSummary {
  hasFavicon: boolean;
}

/**
 * Swaps the document's favicon link to the active organisation's
 * favicon endpoint when the org has actually uploaded one. If not, we
 * leave the default (defined in index.html) in place so the tab does
 * not flash to a broken icon.
 *
 * No render output.
 */
export function FaviconUpdater(): null {
  const { activeOrganisation } = useAuth();

  useEffect(() => {
    if (typeof document === "undefined") return;
    const slug = activeOrganisation?.slug;
    if (!slug) return;

    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) return; // index.html always ships one; defensive
    const previousHref = link.getAttribute("href");
    const previousType = link.getAttribute("type");

    let cancelled = false;
    let didOverride = false;

    void api<{ branding: BrandingSummary }>("/organisations/me/branding")
      .then((res) => {
        if (cancelled) return;
        if (!res.branding.hasFavicon) return;
        const base = import.meta.env.VITE_API_URL ?? "/api/v1";
        link.setAttribute("href", `${base}/branding/${slug}/favicon`);
        // Drop the type hint — we don't know the uploaded MIME from the
        // summary alone; the browser will sniff from the response's
        // Content-Type header which the server sets correctly.
        link.removeAttribute("type");
        didOverride = true;
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      if (!didOverride) return;
      // Restore the default on sign-out / org switch.
      if (previousHref !== null) link.setAttribute("href", previousHref);
      if (previousType !== null) link.setAttribute("type", previousType);
    };
  }, [activeOrganisation?.slug]);

  return null;
}
